import classNames from 'classnames';
import { HTMLAttributes } from 'react';
import { FieldPath, FieldValues, PathValue, useController, UseControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { validateNumber } from '@steelbuy/ts-shared';
import { Icon, IconIdentifier, Tooltip } from '@steelbuy/ui-primitive';
import './NumberInput.scss';

interface NumberInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends UseControllerProps<TFieldValues, TName> {
    inputMode?: Extract<HTMLAttributes<HTMLInputElement>['inputMode'], 'numeric' | 'decimal'>;
    error?: string;
    preInput?: string;
    endAdornment?: string;
    integerOnly?: boolean;
    /** When true, the error prop fully controls error display and fieldState.error is ignored. */
    controlledError?: boolean;
}

export const NumberInput = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
    control,
    inputMode = 'decimal',
    rules,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    error: errorProp,
    preInput,
    endAdornment,
    integerOnly,
    controlledError,
    ...rest
}: NumberInputProps<TFieldValues, TName>) => {
    const { t } = useTranslation('uiDomain');
    const { field, fieldState } = useController({
        control,
        rules: {
            ...(rules || {}),
            validate: (value: PathValue<TFieldValues, TName>, formValues: TFieldValues) => {
                if (typeof value === 'string' && value !== '' && !validateNumber(value)) {
                    return t('validation.numberValue');
                }
                if (rules?.validate) {
                    if (typeof rules.validate === 'function') {
                        return rules.validate(value, formValues);
                    }
                    if (typeof rules.validate === 'object') {
                        const result = (
                            Object.values(rules.validate) as Array<(v: unknown, fv: TFieldValues) => string | true>
                        ).reduce<string | true>((acc, fn) => (acc !== true ? acc : fn(value, formValues)), true);
                        if (result !== true) return result;
                    }
                }
                return true;
            },
        },
        ...rest,
    });

    // When controlledError=true the caller fully owns error display (touch-gating etc).
    // Otherwise use the field's own validation state, falling back to the error prop.
    const error = controlledError ? errorProp : fieldState.error?.message ?? errorProp;

    const formInputClassMap = {
        'form-input': true,
        'form-input--disabled': false,
        'form-input--with-adornment': !!endAdornment,
    };

    const formInputFieldClassMap = {
        'form-input__field': true,
        'form-input__field--disabled': false,
        'form-input__field--error': !!error,
        'form-input__field--error--endAdornment': !!endAdornment && !!error,
        'form-input__field--read-only': false,
    };
    const formInputFieldInputClassMap = {
        'form-input__field__input': true,
        'form-input__field__input--disabled': false,
        'form-input__field__input--read-only': false,
        'form-input__field__input--error': !!error && !endAdornment,
        'form-input__field__input--has-pre-input': !!preInput,
        'form-input__field__input--has-end-adornment': !!endAdornment,
    };

    return (
        <div className={classNames(formInputClassMap)}>
            <span className={classNames(formInputFieldClassMap)}>
                {preInput && <span className="form-input__field__pre-input">{preInput}</span>}
                <input
                    {...field}
                    type="text"
                    className={classNames(formInputFieldInputClassMap)}
                    inputMode={inputMode}
                    onChange={(e) => {
                        if (integerOnly) {
                            const cleaned = e.currentTarget.value.replace(/\D/g, '');
                            field.onChange(cleaned);
                        } else {
                            field.onChange(e.currentTarget.value);
                        }
                    }}
                />
                {endAdornment && <span className="form-input__field__end-adornment">{endAdornment}</span>}
                {!!error && (
                    <Tooltip content={error}>
                        <span className={classNames('icon__position', { 'icon__position--inline': !!endAdornment })}>
                            <Icon name={IconIdentifier.ERROR} />
                        </span>
                    </Tooltip>
                )}
            </span>
        </div>
    );
};
